import React, { useRef, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { Icons } from "../../components/ui/icons";
import TabButton from "../../components/tab-button";
import TabBar from "../../components/tab-bar";
import PageShell from "../../components/page-shell";
import { RefreshToolbarButton } from "../../components/refresh-toolbar-button";
import ShopSection, { type ShopSectionHandle } from "./shop/ShopSection";
import ServicesSection, { type ServicesSectionHandle } from "./services/ServicesSection";
import { MOBILE_APP_TABS, type MobileAppTab, isShopTab } from "./constants";

const MobileApp: React.FC = () => {
  const queryClient = useQueryClient();
  const shopRef = useRef<ShopSectionHandle>(null);
  const servicesRef = useRef<ServicesSectionHandle>(null);
  const [activeTab, setActiveTab] = useState<MobileAppTab>("Categories");

  const shopFetching = useIsFetching({ queryKey: ["shop"] }) > 0;
  const servicesFetching = useIsFetching({ queryKey: ["site-services"] }) > 0;
  const isRefreshing = isShopTab(activeTab) ? shopFetching : servicesFetching;

  const handleRefresh = async () => {
    if (isShopTab(activeTab)) {
      await queryClient.invalidateQueries({ queryKey: ["shop"] });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["site-services"] });
  };

  const createAction =
    activeTab === "Categories"
      ? { label: "Add Category", onClick: () => shopRef.current?.openCreateCategory() }
      : activeTab === "Products"
        ? { label: "Add Product", onClick: () => shopRef.current?.openCreateProduct() }
        : activeTab === "Service Types"
          ? { label: "Add Service Type", onClick: () => servicesRef.current?.openCreateType() }
          : null;

  return (
    <PageShell
      title="Mobile App"
      description="Manage mobile shop catalog, orders, and customer service requests."
      toolbar={
        <>
          <RefreshToolbarButton isRefreshing={isRefreshing} onRefresh={handleRefresh} />
          {createAction && (
            <Button className="h-9" onClick={createAction.onClick}>
              {createAction.label}
            </Button>
          )}
        </>
      }
      mobile={{
        onRefresh: handleRefresh,
        primaryAction: createAction
          ? { label: createAction.label, icon: Icons.Plus, onClick: createAction.onClick }
          : undefined,
      }}
    >
      <TabBar>
        {MOBILE_APP_TABS.map((tab) => (
          <TabButton
            key={tab.id}
            label={tab.label}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          />
        ))}
      </TabBar>

      <ShopSection ref={shopRef} activeTab={activeTab} />
      <ServicesSection ref={servicesRef} activeTab={activeTab} />
    </PageShell>
  );
};

export default MobileApp;
